/**
 * Guarda de permiso de la pantalla de Actividad de usuarios.
 *
 * La ruta ya cuelga de `RequireAuth`; el gating de permiso lo hace la pantalla
 * (mismo patrón que `team/RequirePermission` o el `add_orders` de las órdenes).
 * Replica la paridad con el SSR (`docs/frontend_architecture.md`, Pr. 5): la sección
 * "Análisis" exige `advanced_stats` (mismo permiso que la API y el menú); sin él se
 * muestra un aviso en vez del contenido.
 */
import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { ADVANCED_STATS_PERMISSION } from "../constants";

interface RequireAdvancedStatsProps {
  children: ReactNode;
}

export function RequireAdvancedStats({ children }: RequireAdvancedStatsProps) {
  const { status, hasPermission } = useAuth();

  // Mientras la sesión se hidrata (`/auth/me`), `hasPermission` aún no conoce los
  // permisos: mostrar el aviso "sin permiso" en ese instante sería engañoso. Se
  // espera a tener la sesión resuelta (la ruta ya está protegida por `RequireAuth`).
  if (status === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Actividad de usuarios</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500" role="status">
            Cargando…
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!hasPermission(ADVANCED_STATS_PERMISSION)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Actividad de usuarios</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600" role="alert">
            No tienes permiso para ver la actividad de usuarios.
          </p>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
}
